package com.karyadina.model

import com.karyadina.ui.theme.StudyPalette
import androidx.compose.ui.graphics.Color
/**
 * Feature categories
 */
enum class FeatureCategory(val displayName: String, val description: String) {
    DOCUMENT_TOOLS("Document Tools", "Image & PDF utilities"),
    PRODUCTIVITY("Productivity", "Study & task management"),
    UTILITIES("Utilities", "Quick tools & helpers")
}

/**
 * Main features of kAryadina app
 */
enum class AppFeature(
    val displayName: String, 
    val description: String, 
    val icon: String,
    val category: FeatureCategory
) {
    // Document Tools
    IMAGE_TO_PDF(
        "Image to PDF",
        "Convert multiple images to a single PDF document",
        "picture_as_pdf",
        FeatureCategory.DOCUMENT_TOOLS
    ),
    IMAGE_RESIZER(
        "Image Resizer",
        "Resize and compress images to specific sizes",
        "photo_size_select_large",
        FeatureCategory.DOCUMENT_TOOLS
    ),
    TRANSLATOR(
        "Image/PDF Translator",
        "Translate text from images and PDFs",
        "translate",
        FeatureCategory.DOCUMENT_TOOLS
    ),
    MAGIC_TEXT_SCANNER(
        "Magic Text Scanner",
        "Scan documents and extract text instantly",
        "document_scanner",
        FeatureCategory.DOCUMENT_TOOLS
    ),
    
    // Productivity Tools for Students
    POMODORO_TIMER(
        "Focus Timer",
        "Pomodoro technique for better focus",
        "timer",
        FeatureCategory.PRODUCTIVITY
    ),
    TASK_TRACKER(
        "Task Tracker",
        "Track tasks with deadlines & priorities",
        "checklist",
        FeatureCategory.PRODUCTIVITY
    ),
    STUDY_PLANNER(
        "Study Planner",
        "Plan and schedule study sessions",
        "calendar_today",
        FeatureCategory.PRODUCTIVITY
    ),
    QUICK_NOTES(
        "Quick Notes",
        "Capture ideas & notes instantly",
        "note_add",
        FeatureCategory.PRODUCTIVITY
    ),
    FLASHCARDS(
        "Flashcards",
        "Spaced repetition for GATE/NET/UPSC prep",
        "style",
        FeatureCategory.PRODUCTIVITY
    );
    
    companion object {
        fun getByCategory(category: FeatureCategory): List<AppFeature> {
            return entries.filter { it.category == category }
        }

        fun getByPersona(persona: UserPersona, category: FeatureCategory): List<AppFeature> {
            val allFeatures = getByCategory(category)
            val priorities = persona.priorityFeatures
            
            return allFeatures.sortedBy { feature ->
                if (priorities.contains(feature)) 0 else 1
            }
        }
    }
}

/**
 * Theme Mode configuration
 */
enum class ThemeMode(val displayName: String) {
    SYSTEM("System"),
    LIGHT("Light"),
    DARK("Dark")
}


/**
 * App Design System Colors
 * (Mapped to ui.theme.Colors via Theme.kt)
 */

enum class AppThemeColor(val displayName: String, val color: Color) {
    GREEN("Green", StudyPalette.Green500),
    BLUE("Blue", StudyPalette.Blue500),
    ORANGE("Orange", StudyPalette.Orange500),
    PURPLE("Purple", StudyPalette.Purple500),
    CYAN("Cyan", StudyPalette.Cyan500);

    companion object {
        // Helper to find by hex if needed (though likely unused now)
        fun getByHex(hex: Long): AppThemeColor? = entries.find { it.color.value.toLong() == hex }
    }
}


/**
 * User Persona for customized experience
 */
enum class UserPersona(
    val displayName: String, 
    val description: String, 
    val defaultTheme: AppThemeColor
) {
    STUDENT("Student", "School / College / Competitive Exams", AppThemeColor.GREEN),
    PROFESSIONAL("Professional", "Office / Corporate / Business", AppThemeColor.BLUE),
    DEVELOPER("Developer / IT", "Coding / Technical Docs", AppThemeColor.ORANGE),
    CONTENT_CREATOR("Content Creator", "Social Media / Design / Editing", AppThemeColor.PURPLE),
    RESEARCHER("Researcher", "Academic / Scientific / Analysis", AppThemeColor.CYAN),
    GENERAL("General", "Explore everything", AppThemeColor.GREEN);
    
    // Legacy support for on-the-fly colors if needed, but preferable to use enum
    val themeColor: Long get() = defaultTheme.color.value.toLong()

    val priorityFeatures: List<AppFeature>
        get() = when (this) {
            STUDENT -> listOf(AppFeature.STUDY_PLANNER, AppFeature.FLASHCARDS, AppFeature.POMODORO_TIMER, AppFeature.TASK_TRACKER)
            PROFESSIONAL -> listOf(AppFeature.TASK_TRACKER, AppFeature.QUICK_NOTES, AppFeature.POMODORO_TIMER, AppFeature.IMAGE_TO_PDF)
            DEVELOPER -> listOf(AppFeature.QUICK_NOTES, AppFeature.IMAGE_RESIZER, AppFeature.IMAGE_TO_PDF, AppFeature.TASK_TRACKER)
            CONTENT_CREATOR -> listOf(AppFeature.IMAGE_RESIZER, AppFeature.IMAGE_TO_PDF, AppFeature.QUICK_NOTES, AppFeature.TASK_TRACKER)
            RESEARCHER -> listOf(AppFeature.MAGIC_TEXT_SCANNER, AppFeature.TRANSLATOR, AppFeature.STUDY_PLANNER, AppFeature.QUICK_NOTES)
            GENERAL -> AppFeature.entries // Show ALL features
        }
}

/**
 * App configuration
 */
data class AppConfig(
    val version: String = "1.0.0",
    val supportedImageFormats: List<String> = listOf("image/jpeg", "image/png", "image/webp"),
    val maxImageSizeMB: Int = 10,
    val maxImagesForPdf: Int = 50,
    val defaultPdfQuality: Int = 85,
    val defaultImageQuality: Int = 85
)
